import { useState } from 'react';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import { getDisplayError } from '@utils/get-display-error';
import { SpinnerBlock } from '../../components/Spinner';
import { Alert } from '../../components/alert/Alert';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/button/Button';
import { Input } from '../../components/input/Input';
import { Pill, type PillColor } from '../../components/pill/Pill';

const TOKEN_STORAGE_KEY = 'dev_login_token';

type DevUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  isInternal: boolean;
  customers: { customer: { id: number; name: string } }[];
  carriers: { supplier: { id: number; name: string } }[];
};

async function fetchWithToken<T>(url: string, token: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'X-Dev-Token': token,
      ...opts.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message ?? body?.error ?? `Request failed: ${res.status}`);
  }
  return res.json();
}

function getPortalRedirect(user: DevUser): string {
  if (user.isInternal) return '/internal';
  if (user.customers.length === 1) return `/customer-portal/${user.customers[0].customer.id}/orders`;
  if (user.customers.length > 1) return '/customer-portal/select';
  if (user.carriers.length === 1) return `/carrier-portal/${user.carriers[0].supplier.id}`;
  if (user.carriers.length > 1) return '/carrier-portal/select';
  return '/portal/login';
}

function getPortalLabel(user: DevUser): string {
  if (user.isInternal) return 'Internal';
  if (user.customers.length > 0) return 'Customer Portal';
  if (user.carriers.length > 0) return 'Carrier Portal';
  return 'Unknown';
}

function getPortalPillColor(user: DevUser): PillColor {
  if (user.isInternal) return 'info';
  if (user.customers.length > 0) return 'primary';
  if (user.carriers.length > 0) return 'positive';
  return 'neutral';
}

function UserCard({ user, token }: { user: DevUser; token: string }) {
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      await fetchWithToken('/api/v1/dev/login-as', token, {
        method: 'POST',
        body: JSON.stringify({ userId: user.id }),
      });
      globalThis.location.href = getPortalRedirect(user);
    } catch (err) {
      toast.error(getDisplayError(err));
      setIsLoading(false);
    }
  };

  const entityName = user.customers[0]?.customer.name ?? user.carriers[0]?.supplier.name ?? null;

  return (
    <button
      onClick={handleLogin}
      disabled={isLoading}
      className="w-full text-left bg-surface border border-border rounded-lg p-4 hover:border-border-strong hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-wait"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-fg truncate">{user.name}</p>
          <p className="text-sm text-fg-muted truncate">{user.email}</p>
          {entityName && <p className="text-sm text-fg-secondary mt-0.5 truncate">{entityName}</p>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Pill color={getPortalPillColor(user)} size="sm">
            {getPortalLabel(user)}
          </Pill>
          <span className="text-xs text-fg-muted">{user.role}</span>
        </div>
      </div>
    </button>
  );
}

const PORTAL_ORDER = ['Internal', 'Customer Portal', 'Carrier Portal', 'Unknown'];

function UserList({ token }: { token: string }) {
  const {
    data: users,
    isLoading,
    error,
  } = useSWR(['dev/users', token], () => fetchWithToken<DevUser[]>('/api/v1/dev/users', token), {
    revalidateOnFocus: false,
  });

  if (isLoading) return <SpinnerBlock message="Loading users..." />;

  if (error) {
    return <Alert type="error">{getDisplayError(error)}</Alert>;
  }

  const grouped = (users ?? []).reduce<Record<string, DevUser[]>>((acc, user) => {
    const label = getPortalLabel(user);
    if (!acc[label]) acc[label] = [];
    acc[label].push(user);
    return acc;
  }, {});

  if (users?.length === 0) {
    return (
      <p className="text-fg-muted text-sm">
        No users found. Run <code className="bg-neutral-subtle px-1 rounded">node scripts/seed.mjs</code> to create test
        users.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {PORTAL_ORDER.filter((label) => grouped[label]?.length).map((label) => (
        <section key={label}>
          <h2 className="label-text mb-3">{label}</h2>
          <div className="flex flex-col gap-2">
            {grouped[label].map((user) => (
              <UserCard key={user.id} user={user} token={token} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function DevLoginPage() {
  const [savedToken, setSavedToken] = useState(() => globalThis.localStorage.getItem(TOKEN_STORAGE_KEY) ?? '');
  const [inputValue, setInputValue] = useState(savedToken);

  const saveToken = () => {
    const trimmed = inputValue.trim();
    globalThis.localStorage.setItem(TOKEN_STORAGE_KEY, trimmed);
    setSavedToken(trimmed);
  };

  return (
    <>
      <PageHeader title="Dev Login" />
      <main className="min-h-screen bg-surface-sunken p-8">
        <div className="max-w-xl mx-auto">
          <div className="mb-8">
            <h1 className="heading-one">Dev Login</h1>
            <p className="text-fg-muted mt-1">Click any user to instantly log in. Requires a valid dev token.</p>
          </div>

          <div className="mb-8 p-4 border border-border rounded-lg bg-surface">
            <label className="label-text block mb-2">
              Dev access token{' '}
              <span className="font-regular text-fg-muted">
                (<code>DEV_LOGIN_TOKEN</code> env var)
              </span>
            </label>
            <div className="flex gap-2">
              <Input
                type="password"
                value={inputValue}
                onChange={setInputValue}
                onKeyDown={(e) => e.key === 'Enter' && saveToken()}
                placeholder="Paste token here…"
                className="flex-1"
              />
              <Button color="secondary" onTrigger={saveToken}>
                Save
              </Button>
            </div>
          </div>

          {savedToken ? (
            <UserList token={savedToken} />
          ) : (
            <p className="text-fg-muted text-sm">Enter a token above to see available users.</p>
          )}
        </div>
      </main>
    </>
  );
}
